import { useEffect, useState } from "react";
import { Ban, ShieldAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Textarea } from "./ui/textarea";

/**
 * Branded confirmation dialog for Ban / Unban actions. Replaces the raw
 * `window.prompt` fallback so bans feel deliberate + on-brand.
 *
 * Props:
 *   user           — { id, name, email, banned, banned_reason } target
 *   nextBanned     — the state we are moving to (true = banning, false = unbanning)
 *   open           — controlled visibility
 *   onOpenChange   — parent's setter
 *   onConfirm      — called with (reason: string|null) when the user confirms
 */
export default function BanConfirmDialog({ user, nextBanned, open, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const banning = !!nextBanned;
  const displayName = user?.name || user?.email || "this user";

  useEffect(() => {
    if (open) setReason("");
  }, [open, user?.id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onConfirm(banning ? reason.trim() || null : null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="bg-white border border-slate-200 rounded-none text-slate-900"
        data-testid="ban-confirm-dialog"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
            {banning ? (
              <><Ban className="w-4 h-4 text-orange-400" /> Ban {displayName}?</>
            ) : (
              <><ShieldAlert className="w-4 h-4 text-emerald-400" /> Unban {displayName}?</>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600 text-sm mt-2 space-y-2">
            {banning ? (
              <>
                <span className="block">
                  They will be immediately signed out of active sessions and blocked from logging back in.
                  Any reason you enter below is shown on their next login attempt.
                </span>
              </>
            ) : (
              <span className="block">
                Restore this account? They&#39;ll be able to sign in again on their next attempt.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {banning && (
          <div className="mt-1">
            <label className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500 block mb-1">
              // Reason <span className="text-slate-400 normal-case tracking-normal">(optional, max 500)</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Repeated harassment in Community"
              rows={3}
              className="bg-slate-50 border-slate-200 rounded-none resize-none text-sm"
              data-testid="ban-confirm-reason"
              autoFocus
            />
            <div className="text-right font-mono-num text-[10px] text-slate-400 mt-1">
              {reason.length}/500
            </div>
          </div>
        )}

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel
            className="rounded-none border-slate-200 bg-transparent text-slate-800 hover:bg-slate-50 uppercase tracking-widest text-xs"
            data-testid="ban-confirm-cancel"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={submit}
            disabled={submitting}
            className={`rounded-none uppercase tracking-widest text-xs font-semibold ${
              banning
                ? "bg-orange-500 hover:bg-orange-600 text-black"
                : "bg-emerald-500 hover:bg-emerald-600 text-black"
            }`}
            data-testid="ban-confirm-submit"
          >
            {submitting ? "Working…" : banning ? "Ban member" : "Unban member"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
